import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { PaymentService } from 'src/app/core/services/payment.service';
import { UtilService } from 'src/app/core/services/util.service';
import { FeeSummary, FeeItem } from 'src/app/core/models/fee.model';

/** A selectable payment option in the stand-alone fee dropdown. */
interface PaymentOption {
  id: string;
  label: string;
  amount: number;
  /** Text recorded on the fee summary and payment receipt. */
  description: string;
}

@Component({
  selector: 'app-standalone-payment',
  templateUrl: './standalone-payment.component.html',
  styleUrls: ['./standalone-payment.component.scss'],
})
export class StandalonePaymentComponent implements OnInit {
  readonly UNIT_PRICE: number = 38.00;

  private readonly QUANTITY_WORDS: string[] = [
    'One', 'Two', 'Three', 'Four', 'Five',
    'Six', 'Seven', 'Eight', 'Nine', 'Ten',
  ];

  selectedOptionId: string | null = null;
  amount: number = 0;
  isProcessing: boolean = false;

  private readonly LIFETIME_RETIRED_PRICES: { count: number; amount: number }[] = [
    { count: 1, amount: 162.00 },
    { count: 2, amount: 200.00 },
    { count: 3, amount: 238.00 },
  ];

  paymentOptions: PaymentOption[] = [
    ...this.buildCertificateOptions(),
    ...this.buildLifetimeRetiredOptions(),
  ];

  constructor(
    private paymentService: PaymentService,
    public util: UtilService,
    public router: Router
  ) {}

  ngOnInit(): void {
    // Component initialization - no localStorage needed
  }

  onOptionChange(): void {
    this.amount = this.getSelectedOption()?.amount ?? 0;
  }

  isFormValid(): boolean {
    return this.getSelectedOption() !== undefined;
  }

  private getSelectedOption(): PaymentOption | undefined {
    if (this.selectedOptionId === null) {
      return undefined;
    }
    return this.paymentOptions.find((opt) => opt.id === this.selectedOptionId);
  }

  /**
   * Builds the per-certificate quantity options (quantity x unit price).
   * Labels are generated so they cannot drift from the amounts charged.
   */
  private buildCertificateOptions(): PaymentOption[] {
    return this.QUANTITY_WORDS.map((word, index) => {
      const quantity = index + 1;
      const amount = quantity * this.UNIT_PRICE;
      return {
        id: `CERT_${quantity}`,
        label: `${word} ($${amount.toFixed(2)})`,
        amount,
        description: `Certification Fee (${quantity} item${quantity > 1 ? 's' : ''})`,
      };
    });
  }

  /**
   * Builds the flat-price lifetime retired options. Amounts are fixed (not
   * quantity x unit price), so each is listed explicitly.
   */
  private buildLifetimeRetiredOptions(): PaymentOption[] {
    return this.LIFETIME_RETIRED_PRICES.map(({ count, amount }) => ({
      id: `LIFETIME_RETIRED_${count}`,
      label: `Lifetime Retired Option ${count} ($${amount.toFixed(2)})`,
      amount,
      description: `Lifetime Retired Option ${count}`,
    }));
  }

  async submitPayment(): Promise<void> {
    const option = this.getSelectedOption();
    if (!option) {
      this.util.openToastr('error', 'Please select a payment amount');
      return;
    }

    this.isProcessing = true;

    const description = option.description;

    try {
      this.paymentService
        .createStandalonePayment(description, option.amount)
        .subscribe({
          next: (response: any) => {
            if (response.success && response.feeSummaryId) {
              const feeSummary = new FeeSummary();
              feeSummary.feeSummaryId = response.feeSummaryId;
              feeSummary.transactionId = response.transactionId;
              feeSummary.appId = response.appId || 'STANDALONE';
              feeSummary.userId = response.userId;
              feeSummary.feeTotal = response.feeTotal;
              feeSummary.feeTotalPaid = response.feeTotalPaid || 0;
              feeSummary.balanceAmount = response.balanceAmount;
              feeSummary.items = response.items?.map((item: any) => {
                const feeItem = new FeeItem();
                feeItem.name = item.description;
                feeItem.feeAmount = item.unitPrice;
                feeItem.feeAmountPaid = 0;
                return feeItem;
              }) || [];

              // Backend stores FeeSummary - no localStorage needed
              this.preparePayment(feeSummary);
            } else {
              this.isProcessing = false;
              this.util.openToastr(
                'error',
                response.errorMessage || 'Failed to create payment'
              );
            }
          },
          error: (err) => {
            this.isProcessing = false;
            this.util.openToastr('error', 'Error creating payment. Please try again.');
            console.error('Create payment error:', err);
          },
        });
    } catch (error) {
      this.isProcessing = false;
      this.util.openToastr('error', 'An unexpected error occurred');
      console.error('Submit payment error:', error);
    }
  }

  private preparePayment(feeSummary: FeeSummary): void {
    // Line items are built by backend from FeeSummary - no payload needed
    // Backend saves token to FeeSummary - no localStorage needed
    this.paymentService
      .prepareStandalonePayment(feeSummary.feeSummaryId, feeSummary.appId)
      .subscribe({
        next: (response: any) => {
          if (response.success && response.redirectUrl) {
            // Redirect to Tyler - backend has already saved token to FeeSummary
            document.location.href = response.redirectUrl;
          } else if (response.fullyCoveredByCredits) {
            this.util.openToastr('success', 'Payment completed using available credits');
            this.router.navigate(['app/standalone-payment/confirmation'], {
              queryParams: { feeSummaryId: feeSummary.feeSummaryId, appId: feeSummary.appId },
            });
          } else {
            this.isProcessing = false;
            this.util.openToastr(
              'error',
              response.errorMessage || 'Failed to prepare payment'
            );
          }
        },
        error: (err) => {
          this.isProcessing = false;
          this.util.openToastr('error', 'Error preparing payment. Please try again.');
          console.error('Prepare payment error:', err);
        },
      });
  }

  goBack(): void {
    this.router.navigate(['app']);
  }
}
